import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Dropdown from '../../components/Dropdown/Dropdown';
import ButtonSecondary from '../../components/Button/ButtonSecondary';
import ProductTable from '../../components/ProductTable/ProductTable';
import { Screen } from './styles';
import {
  getSites,
  getParties,
  getReceipt,
  getReceiptLineMatches,
  linkReceiptLine,
  unlinkReceiptLine,
  createReceipt,
  addLot
} from '../../api';
import '../../components/ProductsModal/_styles.products-modal.scss';
import './_styles.receipts.scss';

// Local (not UTC) YYYY-MM-DD, so an early-morning receipt doesn't display as
// the previous day.
const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

// Read-only preview of an existing receipt.
const ReceiptView = ({ id }) => {
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  // The receipt line being linked, and the order items it can link to.
  const [linking, setLinking] = useState(null);
  const [matches, setMatches] = useState(null);

  const load = () =>
    getReceipt(id)
      .then(({ data }) => setReceipt(data))
      .catch((err) => {
        console.error('Failed to load receipt', err);
        setError('Could not load this receipt.');
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openMatches = async (line) => {
    setLinking(line);
    setMatches(null);
    const { data } = await getReceiptLineMatches(id, line.id);
    setMatches(data);
  };

  const link = async (poLineId) => {
    try {
      await linkReceiptLine(id, linking.id, poLineId);
      setLinking(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not link this line.');
    }
  };

  const unlink = async (line) => {
    const confirmed = window.confirm(
      `Unlink ${line.product || 'this line'} from purchase order ${line.po}?`
    );
    if (!confirmed) return;

    try {
      await unlinkReceiptLine(id, line.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not unlink this line.');
    }
  };

  return (
    <div className='receipts'>
      <div className='receipt-header'>
        <h3>Receipt #{id}</h3>
        <ButtonSecondary title='Back' onClick={() => navigate('/receipts')} />
      </div>

      <div className='receipt-view'>
        {error ? (
          <p className='muted'>{error}</p>
        ) : !receipt ? (
          <p className='muted'>Loading...</p>
        ) : (
          <>
            <div className='detail-grid'>
              <div className='detail'>
                <span className='label'>Date</span>
                <span>{formatDate(receipt.date)}</span>
              </div>
              <div className='detail'>
                <span className='label'>Supplier</span>
                <span>{receipt.supplier || '-'}</span>
              </div>
              <div className='detail'>
                <span className='label'>Site</span>
                <span>{receipt.site || '-'}</span>
              </div>
              <div className='detail'>
                <span className='label'>Quantity</span>
                <span>{receipt.quantity ?? '-'}</span>
              </div>
            </div>

            <div className='receipt-lines'>
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Lot</th>
                    <th>Quantity</th>
                    <th>Purchase Order</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.lines.length === 0 ? (
                    <tr>
                      <td colSpan={4} className='empty'>
                        No products on this receipt.
                      </td>
                    </tr>
                  ) : (
                    receipt.lines.map((line) => (
                      <tr key={line.id}>
                        <td>{line.product || '-'}</td>
                        <td>{line.lot || '-'}</td>
                        <td>{line.quantity}</td>
                        <td>
                          {line.po ? (
                            <span className='po-linked'>
                              {line.po}
                              <button
                                className='unlink-po'
                                title='Unlink purchase order'
                                onClick={() => unlink(line)}
                              >
                                Unlink
                              </button>
                            </span>
                          ) : (
                            <button
                              className='link-po'
                              onClick={() => openMatches(line)}
                            >
                              Link to PO
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {linking && (
        <div className='detail-modal' style={{ display: 'block' }}>
          <div className='detail-modal-content'>
            <div className='modal-header'>
              <h3>Link {linking.product} to a purchase order item</h3>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Purchase Order</th>
                  <th>Ordered</th>
                  <th>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {!matches ? (
                  <tr>
                    <td colSpan={3}>Loading...</td>
                  </tr>
                ) : matches.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No open orders for this product.</td>
                  </tr>
                ) : (
                  matches.map((match) => (
                    <tr
                      key={match.po_line_id}
                      onClick={() => link(match.po_line_id)}
                    >
                      <td>{match.po}</td>
                      <td>{match.ordered}</td>
                      <td>{match.outstanding}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className='modal-footer'>
              <ButtonSecondary title='Cancel' onClick={() => setLinking(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Capture form for a new receipt.
const ReceiptCapture = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedSite, setSelectedSite] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(0);

  useEffect(() => {
    getSites(JSON.parse(localStorage.getItem('profile')).partyId).then(
      ({ data }) => {
        setSites(
          data.map((site) => {
            return {
              key: site.name,
              value: site.id
            };
          })
        );
      }
    );

    getParties(true).then(({ data }) => {
      setParties(
        data.map((site) => {
          return {
            key: site.name,
            value: site.id
          };
        })
      );
    });
  }, []);

  const receive = async (products, callback) => {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const lot = await addLot({
        product_id: product.product_id,
        lot_desc: product.lot_desc,
        barcode: product.barcode,
        mfg_date: product.mfg_date,
        exp_date: product.exp_date
      });

      products[i].lotId = lot.data.id;
    }

    var quantity = 0;

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      quantity += parseInt(product.quantity);
    }

    const requestData = {
      user_id: JSON.parse(localStorage.getItem('profile')).userId,
      supplier_id: selectedSupplier,
      to_site_id: selectedSite,
      quantity: quantity,
      products: products.map((res) => {
        return { id: res.product_id, lot: res.lotId, quantity: res.quantity };
      })
    };

    const { data } = await createReceipt(requestData);

    if (data) {
      alert(data.message);
      callback(data.success);
      if (data.success) navigate('/receipts');
    } else {
      callback(false);
    }
  };

  const selectSite = (e) => {
    setSelectedSite(e.target.value);
  };

  const selectSupplier = (e) => {
    setSelectedSupplier(e.target.value);
  };

  return (
    <div className='receipts'>
      <div className='receipt-header'>
        <h3>New Receipt</h3>
        <ButtonSecondary title='Back' onClick={() => navigate('/receipts')} />
      </div>

      <Screen>
        <div className='dropdowns' style={{ width: '90%' }}>
          <Dropdown
            placeholder='Select a site'
            icon='pin'
            options={sites}
            onChange={selectSite}
          />
          <Dropdown
            placeholder='Select a supplier'
            icon='user'
            options={parties}
            onChange={selectSupplier}
          />
        </div>
        <ProductTable
          canAddProduct={selectedSupplier != 0 && selectedSite != 0}
          onDone={receive}
        />
      </Screen>
    </div>
  );
};

const Receipt = () => {
  const { id } = useParams();
  return id ? <ReceiptView id={id} /> : <ReceiptCapture />;
};

export default Receipt;
